import { forwardRef, ForwardedRef, useRef, useEffect, useState } from "react";
import classNames from "classnames/bind";
import ChevronUp from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronUp24PxOutlined";
import ChevronDown from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronDown24PxOutlined";
import styles from "./Thumbnails.module.scss";
import { MediaItem } from "../Carousel/Carousel";
import { useInView } from "react-intersection-observer";
import { dispatchGalleryMonetateQEvent } from "../../../components/Analytics/dispatchGalleryMonetateQEvent";

type ThumbnailProps = {
  media: MediaItem[];
  scrollToIndex: (index: number) => void;
  selectedInd: number;
  className?: string;
  fullscreen?: boolean;
  transparent?: boolean;
};

export const Thumbnails = forwardRef(
  (
    {
      media,
      scrollToIndex,
      selectedInd,
      className,
      fullscreen,
      transparent,
    }: ThumbnailProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [prevButtonDisabled, setPrevButtonDisabled] = useState(false);
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { ref: firstItemRef, inView: firstItemInView } = useInView({
      root: wrapperRef.current,
      threshold: 1,
    });
    const { ref: lastItemRef, inView: lastItemInView } = useInView({
      root: wrapperRef.current,
      threshold: 1,
    });

    useEffect(() => {
      if (firstItemInView) {
        setPrevButtonDisabled(true);
      } else {
        setPrevButtonDisabled(false);
      }
    }, [firstItemInView]);

    useEffect(() => {
      if (lastItemInView) {
        setNextButtonDisabled(true);
      } else {
        setNextButtonDisabled(false);
      }
    }, [lastItemInView]);

    const handleButtonNextClick = () => {
      if (typeof ref === "function" || !ref) {
        return;
      }

      const distance = ref.current!.clientHeight / 2;
      const scrollTop = ref.current!.scrollTop;

      ref.current!.scrollTo({
        top: scrollTop + distance,
      });

      dispatchGalleryMonetateQEvent(
        "ThumbnailsDownArrow",
        fullscreen ? "imageModal" : "generic",
      );
    };

    const handleButtonPrevClick = () => {
      if (typeof ref === "function" || !ref) {
        return;
      }

      const distance = ref.current!.clientHeight / 2;
      const scrollTop = ref.current!.scrollTop;

      ref.current!.scrollTo({
        top: scrollTop - distance,
        behavior: "smooth",
      });

      dispatchGalleryMonetateQEvent(
        "ThumbnailsUpArrow",
        fullscreen ? "imageModal" : "generic",
      );
    };

    const getRef = (ind: number) => {
      if (ind === 0) {
        return firstItemRef;
      }
      if (ind === media.length - 1) {
        return lastItemRef;
      }
      return null;
    };

    const cx = classNames.bind(styles);

    return (
      <div className={cx(styles.wrapper, className)} ref={wrapperRef}>
        <div className={styles.thumbnails} ref={ref}>
          {media.map((mediaItem, ind) => (
            <button
              key={`${mediaItem.imageUrl}-${ind}`}
              className={cx(styles.thumbnailItem, {
                "thumbnailItem--selected": ind === selectedInd,
                video: mediaItem.type === "video",
                "thumbnailItem--transparent": transparent,
              })}
              onClick={() => {
                scrollToIndex(ind);
                dispatchGalleryMonetateQEvent(
                  "ThumbnailItem",
                  fullscreen ? "imageModal" : "generic",
                );
              }}
              ref={getRef(ind)}
              data-testid={`standard:gallery:thumbnails:thumbnail-${ind}`}
              aria-label={`Product Image ${ind + 1} of ${media.length}`}
            >
              <img
                src={mediaItem.imageUrl}
                alt=""
                className={styles.thumbnailImage}
              />
            </button>
          ))}
        </div>
        <button
          data-testid="standard:gallery:scroll:button:previous"
          className={cx(styles.buttonPrev, {
            disabled: prevButtonDisabled,
          })}
          onClick={handleButtonPrevClick}
          aria-label="Scroll Image Gallery Up"
        >
          <ChevronUp />
        </button>
        <button
          data-testid="standard:gallery:scroll:button:next"
          className={cx(styles.buttonNext, {
            disabled: nextButtonDisabled,
          })}
          onClick={handleButtonNextClick}
          aria-label="Scroll Image Gallery Down"
        >
          <ChevronDown />
        </button>
      </div>
    );
  },
);

Thumbnails.displayName = "Thumbnails";
